import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import CountryAutoComplete, {
  CountryInputsInterface,
} from '@/components/countryAutoComplete/countryAutoComplete';
import Button from '@/components/button/Button';
import { WidthComponent } from '@/components/widthComponent/WidthComponent';
import { styles } from './styles';
import TextField from '@/components/textField/TextField';
import {
  SignUpProps,
  UserSignInPersonalInfoInterface,
} from '../SignUpInterface';
import { SignUpPersonalInfoController as SignUpController } from './SignUpPersonalInfoController';
import { dateFormatter } from '@/utils/dateFormatter';
import { AppError } from '@/utils/AppError';
import { useUserService } from '@/data/services/useUserService';
import WarningField from '@/components/warningField/WarningField';
import { useNavigate } from 'react-router-dom';
import { birthSex } from '@/utils/birthSex';
import { endOfDay, subYears } from 'date-fns';
import { useMask, format } from '@react-input/mask';
import { Control } from 'react-hook-form';
import { Country } from '@/components/countryAutoComplete/countries';
import { ResponsiveCalendar } from '@/components/responsiveCalendar/responsiveCalendar';

const SignUpPersonalInfo = ({
  changeStep,
  user,
  setUser,
  setLoading,
  loading,
  setShowWarning,
  showWarning,
  setWarningMessage,
  warningMessage,
  country,
  setCountry,
}: SignUpProps) => {
  const { t } = useTranslation(['signInPersonalInfo', 'common']);
  const { formatDate } = dateFormatter();
  const { checkEmailAlreadyExist } = useUserService();
  const [nextScreen, setNextScreen] = useState(false);
  const [dropBoxWidth, setDropBoxWidth] = useState('0');
  const sixteenYearsAgo = endOfDay(subYears(new Date(), 16));
  const { formatToDate, formatFromDate } = dateFormatter();
  const [mask, setMask] = useState('');
  const [enter, setEnter] = useState(true);
  const options = {
    mask: mask,
    replacement: '_',
  };
  const currentMask = useMask(options);
  const {
    errors,
    control,
    isValid,
    controlledTextView,
    handleSubmit,
    triggerFieldUpdate,
    trigger,
  } = SignUpController({
    user,
    setUser,
    country,
  });
  const navigate = useNavigate();

  const verifyEmail = () => {
    setLoading(true);
    checkEmailAlreadyExist(user.email)
      .then((data) => {
        // O fato do email ser verificado ou não leva para o mesmo caminho, igual no app
        if (data.exists && data.emailVerified) {
          setWarningMessage(t('create_account_email_already_registered'));
          setShowWarning(true);
        } else if (data.exists && !data.emailVerified) {
          setWarningMessage(t('create_account_email_already_registered'));
          setShowWarning(true);
        } else {
          changeStep(true);
        }
      })
      .catch((error: AppError) => {
        setShowWarning(true);
        setWarningMessage(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (nextScreen) {
      setNextScreen(false);
      verifyEmail();
    }
  }, [user]);

  useEffect(() => {
    if (country) setMask(country?.mask ?? '');
    if (enter) setEnter(false);
  }, [user.countryCode]);

  useEffect(() => {
    if (user.countryCode != '()') {
      trigger('phone');
    }
  }, [mask]);

  const onSubmit = (data: UserSignInPersonalInfoInterface) => {
    setUser({
      ...user,
      name: data.name,
      surname: data.surname,
      birthDate: formatDate(data.birthDate),
      email: data.email,
      gender: data.gender,
      phone: data.phone,
      countryCode: data.countryPhoneCode,
    });
    setNextScreen(true);
  };

  const inputFields = (
    <Stack spacing={2}>
      {controlledTextView(
        <TextField
          title={t('create_account_name')}
          placeholder={t('create_account_name')}
          value={user.name}
          maxLength={50}
          disabled={loading}
        />,
        'name'
      )}
      {controlledTextView(
        <TextField
          title={t('create_account_surname')}
          placeholder={t('create_account_surname')}
          value={user.surname}
          maxLength={50}
          disabled={loading}
        />,
        'surname'
      )}
      <ResponsiveCalendar
        maxDate={sixteenYearsAgo}
        value={user.birthDate == '' ? undefined : formatToDate(user.birthDate)}
        setOnChange={(value: Date | null) => {
          if (value == null) return;
          setUser({ ...user, birthDate: formatFromDate(value) });
        }}
        placeholder={t('create_account_birthdate')}
        disabled={loading}
        controlledTextView={controlledTextView}
        controllerLabel={'birthDate'}
      />
      {controlledTextView(
        <TextField
          list={birthSex()}
          title={t('create_account_gender')}
          placeholder={t('create_account_gender')}
          value={user.gender}
          menuItems={styles.menuItems}
          disabled={loading}
        />,
        'gender'
      )}
      {controlledTextView(
        <TextField
          title={t('create_account_email')}
          placeholder={t('create_account_email')}
          value={user.email}
          maxLength={50}
          disabled={loading}
        />,
        'email'
      )}
      <WidthComponent
        setDropBoxWidth={setDropBoxWidth}
        children={
          <Box sx={styles.phoneContainer}>
            <CountryAutoComplete
              sx={styles.country}
              dropBoxWidth={dropBoxWidth}
              control={
                control as unknown as Control<CountryInputsInterface, any>
              }
              disabeld={loading}
              errors={errors}
              valueDDI={user.countryCode}
              setValueDDI={(value: string, country: Country | null) => {
                setUser({
                  ...user,
                  countryCode: value,
                  phone: '',
                });
                if (country && setCountry) setCountry(country);
              }}
            />
            {controlledTextView(
              <TextField
                sx={styles.phoneField}
                title={t('create_account_phone')}
                placeholder={t('create_account_phone')}
                value={enter ? format(user.phone, options) : user.phone}
                maxLength={
                  //, -,),(
                  country?.length && country.maskLength
                    ? country?.length + country?.maskLength
                    : 14
                }
                ref={country && country?.mask ? currentMask : null}
                numberOnly
                disabled={loading}
              />,
              'phone'
            )}
          </Box>
        }
      />
    </Stack>
  );

  return (
    <Stack
      spacing={4}
      sx={styles.container}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      {inputFields}
      <WarningField
        description={warningMessage}
        isHidden={!showWarning}
        setShowWarning={setShowWarning}
        status="warning"
        leftButtonTitle={t(
          'create_account_email_already_registered_change_email'
        )}
        leftButtonAction={() => {
          setShowWarning(false);
          setUser({
            ...user,
            email: '',
          });
          triggerFieldUpdate('email', '');
        }}
        rightButtonTitle={t(
          'create_account_email_already_registered_resend_password'
        )}
        rightButtonAction={() => {
          setShowWarning(false);
          navigate('/forgot-password');
        }}
      />
      <Button
        title={t('common:next')}
        onClick={handleSubmit(onSubmit)}
        disable={!isValid || loading}
        loading={loading}
      />
    </Stack>
  );
};

export default SignUpPersonalInfo;
